import {
    Box,
    Button,
    Container,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Paper,
    TextField,
    Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AppRoutes, LocalStorage } from '../../helpers'
import { axiosInstance } from '../../helpers/axios'
import { useAppDispatch } from '../../hooks'
import { setRootUser, fetchLeasingContext } from '../../store/User/action'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { passwordValidator } from './ForgotPassword/passwordValidator'
import { PasswordRequirements } from './ForgotPassword/PasswordRequirements'
import { User } from '../../types/user'

const useQuery = () => {
    const { search } = useLocation()
    return useMemo(() => new URLSearchParams(search), [search])
}

const getApiError = (err: unknown, fallback: string) => {
    const axiosError = err as { response?: { data?: { error?: string } } }
    return axiosError.response?.data?.error || fallback
}

export const Register = () => {
    const query = useQuery()
    const inviteToken = query.get('invite')
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [propertyName, setPropertyName] = useState('')
    const [loading, setLoading] = useState(false)
    const [inviteLoading, setInviteLoading] = useState(Boolean(inviteToken))
    const [error, setError] = useState('')
    const [inviteValid, setInviteValid] = useState(true)
    const [accountExists, setAccountExists] = useState(false)

    const passwordValidResult = passwordValidator(password, confirmPassword)

    useEffect(() => {
        if (!inviteToken) {
            setInviteValid(false)
            setInviteLoading(false)
            setError('Invitation link is invalid or missing.')
            return
        }

        localStorage.setItem(LocalStorage.PENDING_INVITE_TOKEN, inviteToken)

        axiosInstance
            .get(`outreach/lead-invitations/${inviteToken}/`)
            .then((res) => {
                setEmail(res.data.email || '')
                setName(res.data.name || '')
                setPropertyName(res.data.property_name || '')
                setAccountExists(Boolean(res.data.account_exists))
            })
            .catch(() => {
                setInviteValid(false)
                setError('This invitation link is invalid or has expired.')
            })
            .finally(() => {
                setInviteLoading(false)
            })
    }, [inviteToken])

    const persistSession = async (userData: User) => {
        const token = userData.token
        if (!token) {
            setError('Unable to sign in. Please try again.')
            return
        }
        localStorage.setItem(LocalStorage.EZTURN_TENANT_TOKEN, token)
        axiosInstance.defaults.headers.common['Authorization'] = `token ${token}`
        dispatch(setRootUser(userData))
        await dispatch(fetchLeasingContext())
        localStorage.removeItem(LocalStorage.PENDING_INVITE_TOKEN)
        navigate(AppRoutes.home)
    }

    const acceptInvite = async (payload: {
        email: string
        password: string
        name?: string
    }) => {
        if (!inviteToken) {
            return
        }

        setLoading(true)
        setError('')

        try {
            const res = await axiosInstance.post(
                `outreach/lead-invitations/${inviteToken}/accept/`,
                payload,
            )
            await persistSession(res.data)
        } catch (err) {
            setError(
                getApiError(
                    err,
                    accountExists
                        ? 'Incorrect email or password'
                        : 'Unable to create account. Please check your details and try again.',
                ),
            )
        } finally {
            setLoading(false)
        }
    }

    const handleCreateAccount = () => {
        if (!passwordValidResult.passwordValid || !name) {
            return
        }
        acceptInvite({ email, password, name })
    }

    const handleSignIn = () => {
        if (!password) {
            return
        }
        acceptInvite({ email, password })
    }

    const resetPasswordHref = `${AppRoutes.resetPassword}?invite=${
        inviteToken || ''
    }&email=${encodeURIComponent(email)}`

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f5f5f5',
            }}
        >
            <Container maxWidth="sm" sx={{ flex: 1, display: 'flex' }}>
                <Paper
                    elevation={3}
                    sx={{
                        width: '100%',
                        my: { xs: 2, sm: 4 },
                        p: { xs: 2, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'center',
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{ fontWeight: 'bold' }}
                    >
                        {inviteLoading
                            ? 'Just a moment'
                            : accountExists
                              ? 'Welcome Back'
                              : 'Create Your Account'}
                    </Typography>
                    {propertyName && !inviteLoading && (
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            {accountExists
                                ? `Sign in to apply at ${propertyName}`
                                : `Apply at ${propertyName}`}
                        </Typography>
                    )}
                    {accountExists && email && !inviteLoading && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            We found an account for {email}. Enter your password to
                            sign in.
                        </Typography>
                    )}

                    {!inviteLoading && (
                    <Box
                        component="form"
                        sx={{ mt: 3 }}
                        onSubmit={(e) => {
                            e.preventDefault()
                            if (accountExists) {
                                handleSignIn()
                            } else {
                                handleCreateAccount()
                            }
                        }}
                    >
                        {!accountExists && (
                            <TextField
                                fullWidth
                                label="Full Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                sx={{ mb: 3 }}
                                disabled={!inviteValid || inviteLoading}
                            />
                        )}

                        <TextField
                            fullWidth
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled
                        />

                        <OutlinedInput
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            disabled={!inviteValid || inviteLoading}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? (
                                            <Visibility />
                                        ) : (
                                            <VisibilityOff />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            }
                            sx={{ mb: 2 }}
                        />

                        {!accountExists && (
                            <>
                                <OutlinedInput
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm Password"
                                    disabled={!inviteValid || inviteLoading}
                                    sx={{ mb: 2 }}
                                />

                                <PasswordRequirements
                                    passwordRequirementResult={passwordValidResult}
                                />
                            </>
                        )}

                        {error && (
                            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                {error}
                            </Typography>
                        )}

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={
                                !inviteValid ||
                                inviteLoading ||
                                loading ||
                                (accountExists
                                    ? !password
                                    : !passwordValidResult.passwordValid || !name)
                            }
                            sx={{
                                mb: 2,
                                mt: 2,
                                backgroundColor: '#008C85',
                                '&:hover': { backgroundColor: '#007c75' },
                            }}
                        >
                            {loading
                                ? accountExists
                                    ? 'Signing in...'
                                    : 'Creating account...'
                                : accountExists
                                  ? 'Sign In'
                                  : 'Create Account'}
                        </Button>

                        {accountExists ? (
                            <Typography variant="body2" align="center">
                                <Link to={resetPasswordHref}>Forgot password?</Link>
                            </Typography>
                        ) : (
                            <Typography variant="body2" align="center">
                                Already have an account?{' '}
                                <Link
                                    to={`${AppRoutes.login}?invite=${inviteToken || ''}`}
                                >
                                    Sign in
                                </Link>
                            </Typography>
                        )}
                    </Box>
                    )}
                </Paper>
            </Container>
        </Box>
    )
}
